import { apiClient } from './api';

export interface FeaturedPostsSettings {
    rewardEnabled: boolean;
    rewardPoints: number;
    uniquePerWeek: boolean;
}

export interface FeaturedPostsData {
    settings: FeaturedPostsSettings;
    weekInfo: {
        weekNumber: number;
        year: number;
        startDate: string;
        endDate: string;
    };
    weekSummary: {
        totalPosts: number;
        uniqueAuthors: number;
        rewardedCount: number;
        skippedCount: number;
        totalPoints: number;
        avgScore: number;
    };
    topAuthors: Array<{
        rank: number;
        authorId: number;
        nickname: string;
        featuredCount: number;
        rewardedCount: number;
        totalPoints: number;
        avgScore: number;
        bestRank: number;
    }>;
    dailyReports: Array<{
        date: string;
        dayName: string;
        postsCount: number;
        authorsCount: number;
        rewardedCount: number;
        skippedCount: number;
        totalPoints: number;
        avgScore: number;
    }>;
}

export interface DailyDetailData {
    date: string;
    dayName: string;
    stats: {
        totalPosts: number;
        totalAuthors: number;
        totalRewards: number;
        rewardedCount: number;
        skippedCount: number;
        avgScore: number;
        maxScore: number;
    };
    posts: Array<{
        rank: number;
        postId: number;
        postCaption: string;
        authorId: number;
        authorName: string;
        likes: number;
        comments: number;
        views: number;
        score: number;
        rewardPoints: number;
        isRewarded: boolean;
        rewardedAt: string | null;
        skipReason: string | null;
        isAdminPick: boolean;
        postUrl: string;
    }>;
}

export interface LeaderboardData {
    week: string;
    startDate: string;
    endDate: string;
    leaderboard: Array<{
        rank: number;
        authorId: number;
        nickname: string;
        featuredCount: number;
        rewardedCount: number;
        totalPoints: number;
        avgScore: number;
        bestRank: number;
    }>;
}

export const featuredPostsService = {
    /**
     * Get main page data
     */
    async getMainData(week?: string): Promise<FeaturedPostsData> {
        const endpoint = week ? `/featured-posts?week=${week}` : '/featured-posts';
        const { camelizedResponse } = await apiClient(endpoint);
        return camelizedResponse.data;
    },

    /**
     * Get settings
     */
    async getSettings(): Promise<FeaturedPostsSettings> {
        const { camelizedResponse } = await apiClient('/featured-posts/settings');
        return camelizedResponse.data;
    },

    /**
     * Update settings
     */
    async updateSettings(settings: {
        rewardEnabled: boolean;
        rewardPoints: number;
        uniquePerWeek: boolean;
    }): Promise<FeaturedPostsSettings> {
        const { camelizedResponse } = await apiClient('/featured-posts/settings', {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                reward_enabled: settings.rewardEnabled,
                reward_points: settings.rewardPoints,
                unique_per_week: settings.uniquePerWeek,
            }),
        });
        return camelizedResponse.data;
    },

    /**
     * Get daily detail
     */
    async getDailyDetail(date: string): Promise<DailyDetailData> {
        const { camelizedResponse } = await apiClient(`/featured-posts/daily/${date}`);
        return camelizedResponse.data;
    },

    /**
     * Get leaderboard
     */
    async getLeaderboard(week: string): Promise<LeaderboardData> {
        const { camelizedResponse } = await apiClient(`/featured-posts/leaderboard?week=${week}`);
        return camelizedResponse.data;
    },

    /**
     * Reprocess a date
     */
    async reprocess(date: string): Promise<{ message: string; output: string }> {
        const { camelizedResponse } = await apiClient(`/featured-posts/reprocess/${date}`, {
            method: 'POST',
        });
        return camelizedResponse;
    },

    /**
     * Export week as CSV
     */
    getExportUrl(week: string): string {
        const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL as string;
        const token = localStorage.getItem('access_token');
        return `${baseUrl}/featured-posts/export/${week}?token=${token}`;
    },
};
